import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PencilSimple, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * EditRowSheet — pluggable edit sheet for any list-row entity.
 * Pass `title`, `fields` (array of {name, label, type?, options?, placeholder?, required?, colSpan?}),
 * `initial` (current row), and `endpoint` (PUT path). Fires `onSaved(updated)` when done.
 *
 * Supported field types: text, textarea, number, date, select, gstin
 */
export default function EditRowSheet({
  open,
  onOpenChange,
  title = "Edit",
  endpoint,        // e.g. `/purchases/${id}`
  initial,
  fields,          // array of {name, label, type, options, placeholder, required, colSpan}
  onSaved,
  transformPayload,// optional (form) => payload — for cost-derived fields etc.
  extraTop,        // optional React node rendered above the form
}) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && initial) {
      const seed = {};
      for (const f of fields) seed[f.name] = initial[f.name] ?? (f.type === "number" ? 0 : "");
      setForm(seed);
    }
  }, [open, initial, fields]);

  const change = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const save = async () => {
    // Basic required-field validation
    for (const f of fields) {
      if (f.required && !String(form[f.name] ?? "").trim()) {
        return toast.error(`${f.label} is required`);
      }
    }
    setSaving(true);
    try {
      const payload = transformPayload ? transformPayload(form) : form;
      const { data } = await api.put(endpoint, payload);
      toast.success("Updated");
      onSaved?.(data);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[520px] sm:max-w-lg lg:w-[min(600px,92vw)] lg:max-w-none overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">
            <PencilSimple size={18} className="text-[#EA580C]" /> {title}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-5 space-y-4" data-testid="edit-row-form">
          {extraTop}

          <div className="grid grid-cols-2 gap-3">
            {fields.map((f) => (
              <FieldRow key={f.name} field={f} value={form[f.name]} onChange={change} />
            ))}
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange?.(false)} className="rounded-sm">Cancel</Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="edit-row-save"
              className="flex-1 rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white"
            >
              <FloppyDisk size={14} className="mr-2" />
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function FieldRow({ field, value, onChange }) {
  const colSpanCls = field.colSpan === 2 || field.type === "textarea" ? "col-span-2" : "col-span-1";
  return (
    <div className={colSpanCls}>
      <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">
        {field.label} {field.required && <span className="text-red-600">*</span>}
      </Label>
      {field.type === "textarea" ? (
        <Textarea
          rows={field.rows || 3}
          value={value ?? ""}
          onChange={(e) => onChange(field.name, e.target.value)}
          placeholder={field.placeholder}
          data-testid={`edit-${field.name}`}
        />
      ) : field.type === "select" ? (
        <Select
          value={String(value ?? "") || "__none__"}
          onValueChange={(v) => onChange(field.name, v === "__none__" ? "" : v)}
        >
          <SelectTrigger data-testid={`edit-${field.name}`}><SelectValue placeholder={field.placeholder || ""} /></SelectTrigger>
          <SelectContent>
            {(field.options || []).map((opt) => {
              const val = opt.value === "" || opt.value == null ? "__none__" : String(opt.value);
              return <SelectItem key={val} value={val}>{opt.label}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      ) : (
        <Input
          type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
          value={value ?? ""}
          onChange={(e) => onChange(field.name, e.target.value)}
          placeholder={field.placeholder}
          data-testid={`edit-${field.name}`}
          className={field.type === "gstin" ? "font-mono uppercase" : ""}
        />
      )}
    </div>
  );
}
